import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { fmtB, fmtDate } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import { AddAdjustmentModal } from '../components/recon/AddAdjustmentModal';
import { AutoMatchModal } from '../components/recon/AutoMatchModal';
import { BankOverview } from '../components/recon/BankOverview';
import { FinalizeModal } from '../components/recon/FinalizeModal';
import { ImportStatementModal } from '../components/recon/ImportStatementModal';
import { MatchWorkspace } from '../components/recon/MatchWorkspace';
import {
  accountStatus,
  latestStatementByAccount,
  num,
  type ReconTab,
} from '../components/recon/recon-helpers';
import { ReconHistory } from '../components/recon/ReconHistory';
import { ReconStatement } from '../components/recon/ReconStatement';
import { useBankAccounts } from '../lib/queries/bank-accounts';
import {
  useAddBankReconAdjustment,
  useAutoMatchStatement,
  useBankStatement,
  useBankStatements,
  useCreateBankStatement,
  useCreateBankStatementLines,
  useFinalizeBankStatement,
} from '../lib/queries/bank-recon';
import { useActiveCompanyId } from '../lib/queries/companies';

export const Route = createFileRoute('/recon')({ component: ReconPage });

export function ReconPage() {
  const [tab, setTab] = useState<ReconTab>('overview');
  const [selAccId, setSelAccId] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [showAuto, setShowAuto] = useState(false);
  const [showClose, setShowClose] = useState(false);

  const companyId = useActiveCompanyId();
  const accountsQ = useBankAccounts(companyId);
  const statementsQ = useBankStatements(companyId);
  const reconciledQ = useBankStatements(companyId, { status: 'RECONCILED' });
  const accounts = accountsQ.data ?? [];

  const latest = useMemo(() => latestStatementByAccount(statementsQ.data), [statementsQ.data]);

  const selStatementId = selAccId ? latest.get(selAccId)?.id : undefined;
  const statementQ = useBankStatement(companyId, selStatementId);
  const autoMatch = useAutoMatchStatement(companyId);
  const createStatement = useCreateBankStatement(companyId);
  const createLines = useCreateBankStatementLines(companyId);
  const addAdjustment = useAddBankReconAdjustment(companyId);
  const finalize = useFinalizeBankStatement(companyId);
  const [showAdj, setShowAdj] = useState(false);

  // KPI (ของจริง)
  const accountCount = accounts.length;
  const reconciledCount = accounts.filter(
    (a) => accountStatus(latest.get(a.id)) === 'reconciled',
  ).length;
  const totalClosing = accounts.reduce((s, a) => s + num(latest.get(a.id)?.closingBalance), 0);
  const totalOpen = accounts.reduce((s, a) => s + (latest.get(a.id)?.openCount ?? 0), 0);
  const reconPct = accountCount ? Math.round((reconciledCount / accountCount) * 100) : 0;

  const tabs: readonly (readonly [ReconTab, string])[] = [
    ['overview', 'ภาพรวมทุกบัญชี'],
    ['match', 'Match Workspace · จับคู่รายการ'],
    ['statement', 'Reconciliation Statement'],
    ['history', 'ประวัติการปิดงวด'],
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['รายงานและภาษี', 'กระทบยอดธนาคาร']}
        title="กระทบยอดเงินฝากธนาคาร"
        actions={
          <>
            <HelpButton guide="recon" />
            <button className="btn" onClick={() => setShowImport(true)}>
              <Icon name="upload" size={14} /> นำเข้า Bank Statement
            </button>
            <button className="btn" onClick={() => setShowAuto(true)}>
              <Icon name="link" size={14} /> Auto-match
            </button>
            <button className="btn primary" onClick={() => setShowClose(true)}>
              <Icon name="check" size={14} /> ปิดยอด
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" /> เงินฝากธนาคารรวม (ยอดปิด Statement)
          </div>
          <div className="value tnum">
            {fmtB(totalClosing)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{accountCount} บัญชี</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} /> กระทบยอดสมบูรณ์
          </div>
          <div className="value tnum" style={{ color: 'var(--positive)' }}>
            {reconciledCount} <span className="unit">/ {accountCount} บัญชี</span>
          </div>
          <div className="trend pos">
            <Icon name="check" size={11} /> {reconPct}% ของบัญชี
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} /> รายการรอจับคู่
          </div>
          <div className="value tnum">
            {totalOpen} <span className="unit">รายการ</span>
          </div>
          <div className="trend down">{accountCount - reconciledCount} บัญชียังไม่ปิด</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--info)' }} /> บัญชีที่ยังไม่ Import
          </div>
          <div className="value tnum">
            {accounts.filter((a) => accountStatus(latest.get(a.id)) === 'no-statement').length}
            <span className="unit"> บัญชี</span>
          </div>
          <div className="trend faint">ของ {accountCount} บัญชี</div>
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {accountsQ.isPending && (
        <div className="card">
          <div className="card-body">กำลังโหลด…</div>
        </div>
      )}
      {accountsQ.error && (
        <div className="card">
          <div className="card-body" style={{ color: 'var(--negative)' }}>
            โหลดข้อมูลบัญชีไม่สำเร็จ
          </div>
        </div>
      )}
      {!accountsQ.isPending && !accountsQ.error && accounts.length === 0 && (
        <div className="card">
          <div className="card-body muted" style={{ padding: 40, textAlign: 'center' }}>
            ยังไม่มีบัญชีธนาคาร — เพิ่มที่ Master Data › ธนาคารและบัญชี
          </div>
        </div>
      )}

      {/* TAB BODIES — Task 3-10 จะแทน placeholder เหล่านี้ด้วย component จริง */}
      {accounts.length > 0 && tab === 'overview' && (
        <BankOverview
          accounts={accounts}
          latest={latest}
          onPick={(id) => {
            setSelAccId(id);
            setTab('match');
          }}
        />
      )}
      {tab === 'match' && (
        <MatchWorkspace
          statement={statementQ.data}
          bankLabel={selAccId ? (latest.get(selAccId)?.bankAccountName ?? '') : ''}
          onAutoMatch={() => selStatementId && autoMatch.mutate(selStatementId)}
          onAddAdjustment={() => setShowAdj(true)}
          autoPending={autoMatch.isPending}
        />
      )}
      {tab === 'statement' && <ReconStatement statement={statementQ.data} />}
      {tab === 'history' && <ReconHistory rows={reconciledQ.data ?? []} />}

      {/* MODALS — Task 6/9/10 แทน placeholder */}
      {showImport && (
        <ImportStatementModal
          accounts={accounts}
          pending={createStatement.isPending}
          onClose={() => setShowImport(false)}
          onCreateStatement={(data) => createStatement.mutateAsync(data)}
          onCreateLines={(id, data) => createLines.mutateAsync({ id, data })}
          onAutoMatch={(id) => autoMatch.mutateAsync(id)}
        />
      )}
      {showAuto && (
        <AutoMatchModal
          statementId={selStatementId ?? null}
          statementLabel={
            selAccId && latest.get(selAccId)
              ? `${latest.get(selAccId)!.bankAccountName} · งวด ${fmtDate(latest.get(selAccId)!.statementDate)}`
              : ''
          }
          result={autoMatch.data}
          isPending={autoMatch.isPending}
          onRun={() => selStatementId && autoMatch.mutate(selStatementId)}
          onClose={() => setShowAuto(false)}
        />
      )}
      {showClose && (
        <FinalizeModal
          statement={statementQ.data}
          isPending={finalize.isPending}
          error={finalize.error instanceof Error ? finalize.error.message : null}
          onFinalize={() =>
            selStatementId &&
            finalize.mutate(selStatementId, { onSuccess: () => setShowClose(false) })
          }
          onClose={() => setShowClose(false)}
        />
      )}
      {showAdj && selStatementId && (
        <AddAdjustmentModal
          statementId={selStatementId}
          isPending={addAdjustment.isPending}
          onClose={() => setShowAdj(false)}
          onSubmit={(data) =>
            addAdjustment.mutate(
              { id: selStatementId, data },
              { onSuccess: () => setShowAdj(false) },
            )
          }
        />
      )}
    </div>
  );
}
